import { useEffect, useRef, useState } from "react";

import type { LibraryListSettings } from "api/schemas/Library";
import type { TypeOption } from "routes/patients/PatientProfile/components/UserCourse/components/LibraryHeader";
import type { SortOption } from "utils/array/sort";
import { getStorageValue, setStorageValue } from "utils/storage";
import type { LibraryListSettingsStorageKey } from "utils/storage/storageConfig";

const arraysEqual = <T>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((val, index) => val === b[index]);
};

const defaultSettings: LibraryListSettings = {
  sort: "a-z",
  limit: 50,
  page: 0,
  filter: {
    equipment: [],
    movement: [],
    position: [],
    preview_joint: "all",
    search_term: "",
    type: "all",
  },
};

export const useLibraryListSettings = (storageKey?: LibraryListSettingsStorageKey, persistSettings?: boolean) => {
  let settings: LibraryListSettings | undefined = storageKey ? getStorageValue(storageKey, true) : undefined;
  if (!settings) {
    settings = defaultSettings;
  }

  const isInitialRender = useRef(true);
  const prevDebouncedSearchTerm = useRef(settings.filter.search_term);
  const prevMovement = useRef(settings.filter.movement);
  const prevPosition = useRef(settings.filter.position);
  const prevPreviewJoint = useRef(settings.filter.preview_joint);
  const prevLimit = useRef(settings.limit);
  const prevPage = useRef(settings.page);
  const prevEquipment = useRef(settings.filter.equipment);
  const prevSeletedSort = useRef(settings.sort);
  const prevSelectedTypeOption = useRef(settings.filter.type);

  const [searchTerm, setSearchTerm] = useState(settings.filter.search_term);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(settings.filter.search_term);
  const [equipment, setEquipment] = useState(settings.filter.equipment);
  const [movement, setMovement] = useState(settings.filter.movement);
  const [position, setPosition] = useState(settings.filter.position);
  const [previewJoint, setPreviewJoint] = useState(settings.filter.preview_joint);
  const [selectedSort, setSelectedSort] = useState<SortOption>(settings.sort);
  const [limit, setLimit] = useState(settings.limit);
  const [selectedTypeOption, setSelectedTypeOption] = useState<TypeOption>(settings.filter.type);
  const [currentPage, setCurrentPage] = useState(settings.page);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      const filterChangedFromPrev =
        prevDebouncedSearchTerm.current !== debouncedSearchTerm ||
        !arraysEqual(prevMovement.current, movement) ||
        !arraysEqual(prevPosition.current, position) ||
        prevPreviewJoint.current !== previewJoint ||
        prevLimit.current !== limit ||
        !arraysEqual(prevEquipment.current, equipment) ||
        prevSeletedSort.current !== selectedSort ||
        prevSelectedTypeOption.current !== selectedTypeOption;

      const hasPageChanged = prevPage.current !== currentPage;

      if (filterChangedFromPrev && !hasPageChanged) {
        setCurrentPage(0);
      }

      if ((filterChangedFromPrev || hasPageChanged) && storageKey && persistSettings) {
        setStorageValue(
          storageKey,
          {
            sort: selectedSort,
            limit,
            page: currentPage,
            filter: {
              equipment,
              movement,
              position,
              preview_joint: previewJoint,
              search_term: debouncedSearchTerm,
              type: selectedTypeOption,
            },
          },
          true
        );
      }
    }

    prevDebouncedSearchTerm.current = debouncedSearchTerm;
    prevMovement.current = movement;
    prevPosition.current = position;
    prevPreviewJoint.current = previewJoint;
    prevLimit.current = limit;
    prevPage.current = currentPage;
    prevEquipment.current = equipment;
    prevSeletedSort.current = selectedSort;
    prevSelectedTypeOption.current = selectedTypeOption;
  }, [
    debouncedSearchTerm,
    movement,
    position,
    previewJoint,
    limit,
    currentPage,
    equipment,
    selectedSort,
    storageKey,
    persistSettings,
    selectedTypeOption,
  ]);

  const resetLibraryListSettings = () => {
    setSearchTerm(defaultSettings.filter.search_term);
    setDebouncedSearchTerm(defaultSettings.filter.search_term);
    setEquipment(defaultSettings.filter.equipment);
    setMovement(defaultSettings.filter.movement);
    setPosition(defaultSettings.filter.position);
    setPreviewJoint(defaultSettings.filter.preview_joint);
    setSelectedSort(defaultSettings.sort);
    setLimit(defaultSettings.limit);
    setSelectedTypeOption(defaultSettings.filter.type);
    setCurrentPage(defaultSettings.page);
  };

  const filterPristine =
    debouncedSearchTerm === defaultSettings.filter.search_term &&
    arraysEqual(equipment, defaultSettings.filter.equipment) &&
    arraysEqual(movement, defaultSettings.filter.movement) &&
    arraysEqual(position, defaultSettings.filter.position) &&
    previewJoint === defaultSettings.filter.preview_joint &&
    selectedSort === defaultSettings.sort &&
    selectedTypeOption === defaultSettings.filter.type;

  return {
    initialSearchTerm: settings.filter.search_term,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    equipment,
    setEquipment,
    movement,
    setMovement,
    position,
    setPosition,
    previewJoint,
    setPreviewJoint,
    selectedSort,
    setSelectedSort,
    limit,
    setLimit,
    selectedTypeOption,
    setSelectedTypeOption,
    currentPage,
    setCurrentPage,
    resetLibraryListSettings,
    filterPristine,
  };
};
